import React, { useState } from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { Feather, Ionicons } from "@expo/vector-icons";

import OptionSelectionModal from "@/Components/PersonalAttributes/Components/Modal/OptionSelectionModal";
import { type AttributeValue } from "@/schemas/attribute/AttributeSet";
import { type AppUserSession } from "@/schemas/auth/session";
import { type IoniconIconName } from "@/schemas/icons";
import { type UserAttributeSet } from "@/schemas/profile/UserAttributeSet";
import ProfileService from "@/services/user/profile";

export interface AttributeCardProps {
  session: AppUserSession;
  title: string;
  icon: IoniconIconName;
  attribute: UserAttributeSet;
  values: AttributeValue[];
}

function Card({ label, iconLabel }: AttributeValue): React.JSX.Element {
  return (
    <View style={styles.card}>
      <Text style={styles.emoticon}>{iconLabel}</Text>
      <Text style={styles.label}>{label}</Text>
    </View>
  );
}

function MultipleSelectionAttributeCard({
  session,
  title,
  icon,
  attribute,
  values,
}: AttributeCardProps): React.JSX.Element {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] =
    useState<AttributeValue[]>(values);

  const updateValues = async (
    selectedItems: AttributeValue[],
  ): Promise<void> => {
    try {
      setSelectedOptions(selectedItems);
      const profileService = new ProfileService(session);
      const selectedOptionIds = selectedItems.map((item) => item.id);
      await profileService.updateAttributes(attribute.id, selectedOptionIds);
    } catch (error) {
      alert(error);
    } finally {
      setShowModal(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.sectionContainer}>
        <Ionicons name={icon} size={24} color="#01579b" />
        <Text style={styles.sectionTitle}>{title}</Text>
        <View style={styles.editButton}>
          <TouchableOpacity
            onPress={() => setShowModal(true)}
            style={styles.container}
            accessibilityLabel="Edit"
            accessibilityRole="button"
          >
            <Feather name="edit" size={15} color="#007AFF" />
          </TouchableOpacity>
        </View>
      </View>
      <View style={styles.grid}>
        {selectedOptions.map((lang, index) => (
          <Card key={index} {...lang} />
        ))}
      </View>
      <OptionSelectionModal
        attribute={attribute}
        values={selectedOptions}
        showModal={showModal}
        closeModal={() => setShowModal(false)}
        updateAttributes={updateValues}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
  sectionContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  sectionTitle: {
    fontSize: 20,
    marginLeft: 5,
    fontWeight: "bold",
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  editButton: {
    left: -5,
  },
  card: {
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: 20,
    elevation: 2,
    flexDirection: "row",
    marginBottom: 8,
    marginRight: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    shadowColor: "#000",
    shadowOffset: { height: 1, width: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 1,
  },
  emoticon: {
    marginRight: 8,
  },
  label: {},
});

export default MultipleSelectionAttributeCard;
